import { useEffect, useState } from "react";
import { ArrowUpRight, Sparkles, GitBranch, Users, Cpu, Check, Bookmark, Mail, Bell, Radio, Zap, ArrowRight, ExternalLink, Phone } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { Mascot } from "@/components/Mascot";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const CATEGORIES = [
  { key: "articles", label: "Articles", icon: Radio, color: "#FF8C42", pulse: "pulse-glow" },
  { key: "experts", label: "Experts", icon: Users, color: "#FFD600", pulse: "pulse-glow" },
  { key: "google", label: "Google", icon: GitBranch, color: "#4EA8FF", pulse: "pulse-glow" },
  { key: "ai", label: "IA & LLM", icon: Cpu, color: "#FFA85C", pulse: "pulse-glow" },
];

const HEADLINES = [
  "AI Overviews en France · YMYL",
  "Core Update Novembre · gagnants",
  "Perplexity Pages · nouveau format",
  "Brevet Google · autorité contextuelle",
  "ChatGPT Search · 40 domaines premium",
  "Mike King · SEO à l'ère des LLM",
  "Lily Ray · 50 sites analysés",
  "r/SEO · disparition featured snippets",
];

const EXPERT_NAMES = [
  { name: "Aleyda Solis", country: "🇪🇸", role: "International SEO" },
  { name: "Lily Ray", country: "🇺🇸", role: "E-E-A-T · Amsive" },
  { name: "Mike King", country: "🇺🇸", role: "iPullRank · LLM" },
  { name: "Kevin Indig", country: "🇺🇸", role: "Growth · Product SEO" },
  { name: "Glenn Gabe", country: "🇺🇸", role: "Core Updates" },
  { name: "Olivier Andrieu", country: "🇫🇷", role: "Abondance" },
  { name: "Sylvain Peyronnet", country: "🇫🇷", role: "Ix-Labs · Algo" },
  { name: "Laurent Bourrelly", country: "🇫🇷", role: "Cocon sémantique" },
  { name: "Marie Haynes", country: "🇨🇦", role: "Pénalités Google" },
  { name: "Cindy Krum", country: "🇺🇸", role: "Mobile · AI" },
  { name: "Martin Splitt", country: "🇨🇭", role: "Google · JS Rendering" },
  { name: "Daniel Roch", country: "🇫🇷", role: "SeoMix · WordPress" },
];

export default function Landing() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => (x + 1) % HEADLINES.length), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen text-[#FAF0DE] overflow-hidden relative">
      <div className="mesh-bg" />

      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-2xl bg-[#0A1225]/80 border-b border-[#FAF0DE]/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
            <Mascot pose="waving" className="w-9 h-9" />
            <Wordmark className="h-6" text="#FAF0DE" accent="#FF8C42" />
          </a>
          <div className="hidden md:flex items-center gap-8 text-sm text-[#FAF0DE]/60">
            <a href="#categories" className="hover:text-white transition-colors">Catégories</a>
            <a href="#comment" className="hover:text-white transition-colors">Comment ça marche</a>
            <a href="/blog" className="hover:text-white transition-colors">Blog</a>
            <a href="#founder" className="hover:text-white transition-colors">Fondatrice</a>
            <span className="chip" style={{ color: "#FFD600", borderColor: "rgba(255,214,0,0.4)" }}>Gratuit · Bêta</span>
          </div>
          <button
            onClick={loginWithGoogle}
            data-testid="nav-login-btn"
            className="text-xs font-mono uppercase tracking-widest px-5 py-2.5 rounded-full bg-[#FAF0DE] text-[#0A1225] hover:bg-[#FF8C42] transition-all"
          >
            Connexion
          </button>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative pt-24 pb-20 px-6">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-8 fade-up">
                <span className="chip relative" style={{ borderColor: "rgba(255,140,66,0.4)", color: "#FF8C42" }}>
                  <span className="relative flex">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF8C42]" />
                    <span className="absolute inset-0 rounded-full bg-[#FF8C42] ping-slow" />
                  </span>
                  Signal actif · {new Date().getFullYear()}
                </span>
                <span className="chip text-[#FAF0DE]/50 hidden sm:inline-flex">
                  <Zap className="w-3 h-3" /> Refresh · 2h
                </span>
              </div>

              <h1
                className="font-display text-6xl sm:text-7xl lg:text-8xl xl:text-[8.5rem] font-black tracking-[-0.045em] leading-[0.86] mb-10 fade-up"
                data-testid="hero-title"
              >
                <span className="block">La veille SEO</span>
                <span className="block text-shimmer">qui veille</span>
                <span className="block text-[#FAF0DE]/30">pour vous.</span>
              </h1>

              <p className="text-xl sm:text-2xl text-[#FAF0DE]/75 leading-snug max-w-xl mb-8 fade-up">
                Toute l'actualité SEO, sans perdre une minute.<br />
                <span className="text-[#FF8C42]">Repérez les changements avant les autres.</span>
              </p>

              <div className="flex flex-wrap items-center gap-4 fade-up">
                <button
                  onClick={loginWithGoogle}
                  data-testid="hero-cta-btn"
                  className="group inline-flex items-center gap-3 bg-[#FF8C42] text-[#0A1225] px-7 py-4 rounded-full font-mono text-sm uppercase tracking-widest hover:bg-[#FAF0DE] transition-all hover:scale-105 shadow-[0_0_50px_-10px_rgba(255,140,66,0.7)]"
                >
                  Accéder gratuitement
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </button>
                <span className="text-xs font-mono uppercase tracking-widest text-[#FFD600]">
                  · Gratuit pendant la bêta ·
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 relative fade-up">
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute inset-8 rounded-full bg-gradient-to-br from-[#FF8C42] via-[#FFA85C] to-[#FFD600] blur-2xl opacity-40 halo" />
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="absolute inset-0 rounded-full border float-slow"
                    style={{
                      borderColor: `rgba(255, 140, 66, ${0.25 - i * 0.06})`,
                      transform: `scale(${1 - i * 0.15})`,
                      animationDelay: `${i * 0.8}s`,
                    }}
                  />
                ))}
                <div className="absolute inset-0 flex items-end justify-center">
                  <Mascot pose="binoculars" className="w-full h-full bob" />
                </div>
                <div className="absolute top-6 -left-4 md:-left-10 chip bg-[#0A1225]/80 backdrop-blur border-[#FF8C42]/40" style={{ color: "#FF8C42" }}>
                  <Radio className="w-3 h-3" /> Signal détecté
                </div>
                <div className="absolute bottom-6 -right-4 md:-right-10 chip bg-[#0A1225]/80 backdrop-blur border-[#4EA8FF]/40" style={{ color: "#4EA8FF" }}>
                  <Sparkles className="w-3 h-3" /> Analyse IA
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "30+", v: "Sources agrégées", c: "#FF8C42" },
              { k: "24h", v: "Cycle IA", c: "#FFD600" },
              { k: "2h", v: "Refresh", c: "#4EA8FF" },
              { k: "0€", v: "Gratuit · bêta", c: "#FFA85C" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-[#132038]/60 border border-[#FAF0DE]/8 backdrop-blur p-5">
                <div className="font-display text-4xl font-black" style={{ color: s.c }}>{s.k}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#FAF0DE]/40 font-mono mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HEADLINES ticker */}
      <section className="relative py-8 border-y border-[#FAF0DE]/5 paper-tape overflow-hidden">
        <div className="marquee-left flex gap-3 mb-3" style={{ width: "max-content" }}>
          {[...HEADLINES, ...HEADLINES].map((h, i) => (
            <span key={i} className="chip font-display font-semibold text-[#FAF0DE] text-base whitespace-nowrap bg-[#132038]/70 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF8C42]" /> {h}
            </span>
          ))}
        </div>
        <div className="marquee-right flex gap-3" style={{ width: "max-content" }}>
          {[...HEADLINES.slice().reverse(), ...HEADLINES.slice().reverse()].map((h, i) => (
            <span key={i} className="chip font-display font-semibold text-[#FAF0DE]/80 text-base whitespace-nowrap bg-[#132038]/70 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4EA8FF]" /> {h}
            </span>
          ))}
        </div>
      </section>

      {/* CATEGORIES — animated icons, no mascot */}
      <section id="categories" className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">/ 4 fréquences</div>
              <h2 className="font-display text-5xl md:text-7xl font-black tracking-tight leading-none">
                Un radar.<br />
                <span className="text-shimmer">Quatre couleurs.</span>
              </h2>
            </div>
            <p className="text-[#FAF0DE]/60 max-w-md text-lg leading-relaxed">
              Chaque catégorie a sa fréquence, sa couleur, ses sources.
              Vous ouvrez celle qui vous intéresse en un clic.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORIES.map((c, i) => (
              <div
                key={c.key}
                data-testid={`category-${c.key}`}
                className="card-lift group relative overflow-hidden rounded-3xl border border-[#FAF0DE]/10 aspect-[3/4] flex flex-col justify-between p-6"
                style={{ background: `linear-gradient(180deg, ${c.color}1E 0%, #132038 60%)` }}
              >
                {/* Ambient blob */}
                <div
                  className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-60 float-med"
                  style={{ background: c.color, animationDelay: `${i * 0.7}s` }}
                />
                {/* Big animated icon centerpiece */}
                <div className="absolute bottom-6 right-6 pointer-events-none">
                  <div className="relative">
                    {/* Halo rings pulsing */}
                    {[0, 1, 2].map(r => (
                      <span
                        key={r}
                        className="absolute inset-0 rounded-full border-2 ping-slow"
                        style={{
                          borderColor: c.color,
                          width: `${80 + r * 20}px`,
                          height: `${80 + r * 20}px`,
                          left: `${-r * 10}px`,
                          top: `${-r * 10}px`,
                          animationDelay: `${r * 0.7}s`,
                        }}
                      />
                    ))}
                    <div
                      className="relative w-20 h-20 rounded-3xl flex items-center justify-center bob"
                      style={{
                        background: `${c.color}22`,
                        border: `2px solid ${c.color}`,
                        boxShadow: `0 0 40px -8px ${c.color}, inset 0 0 20px -10px ${c.color}`,
                      }}
                    >
                      <c.icon className="w-10 h-10" style={{ color: c.color }} strokeWidth={2} />
                    </div>
                  </div>
                </div>
                <div className="relative z-10 flex items-start justify-between">
                  <span className="chip" style={{ color: c.color, borderColor: `${c.color}55`, background: `${c.color}12` }}>
                    Live
                  </span>
                </div>
                <div className="relative z-10">
                  <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-none">
                    {c.label}
                  </h3>
                  <ArrowRight
                    className="w-5 h-5 mt-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-2 transition-all"
                    style={{ color: c.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE — cream editorial */}
      <section id="comment" className="relative py-24">
        <div className="section-cream mx-6 rounded-[2rem] md:mx-12 lg:mx-auto lg:max-w-7xl overflow-hidden">
          <div className="p-10 md:p-16">
            <div className="mb-14 max-w-2xl">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#E56E1F] mb-4">/ Comment ça marche</div>
              <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-none text-[#0A1225]">
                Il regarde,<br />analyse,<br /><span className="text-[#FF8C42]">vous alerte.</span>
              </h2>
            </div>

            <div className="space-y-16 md:space-y-20">
              {[
                { pose: "binoculars", step: "01", title: "Il repère chaque signal.", desc: "Le faucon scrute 30+ sources — Search Engine Land, Google Patents, r/SEO, comptes X d'experts, RSS — toutes les 2 heures.", color: "#FF8C42", reverse: false },
                { pose: "newspaper", step: "02", title: "Il lit tout à votre place.", desc: "Articles longs, threads Twitter, papiers de brevets, discussions communautés — le radar avale tout et hiérarchise par pertinence.", color: "#4EA8FF", reverse: true },
                { pose: "lightbulb", step: "03", title: "Il identifie LA tendance du moment.", desc: "Claude Sonnet 4.5 croise tous les signaux et distille une synthèse actionnable de 150 mots. Chaque matin, à 6h UTC.", color: "#FFD600", reverse: false },
                { pose: "clipboard", step: "04", title: "Il vous livre le récap hebdo.", desc: "Chaque lundi, un digest éditorial en 4 sections : Google & SERP, IA & LLM, Experts, À suivre. Lecture 4 minutes.", color: "#E56E1F", reverse: true },
              ].map((row, i) => (
                <div key={i} data-testid={`step-${row.step}`} className={`grid md:grid-cols-12 gap-8 items-center ${row.reverse ? "md:[direction:rtl]" : ""}`}>
                  <div className="md:col-span-5 [direction:ltr]">
                    <div className="relative aspect-square max-w-xs mx-auto">
                      <div className="absolute inset-4 rounded-full blur-2xl opacity-40 halo" style={{ background: row.color }} />
                      <Mascot pose={row.pose} className="relative w-full h-full bob" />
                    </div>
                  </div>
                  <div className="md:col-span-7 [direction:ltr]">
                    <div className="text-xs font-mono uppercase tracking-[0.3em] mb-3" style={{ color: row.color }}>
                      Étape {row.step}
                    </div>
                    <h3 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-tight text-[#0A1225] mb-4">
                      {row.title}
                    </h3>
                    <p className="text-lg text-[#0A1225]/70 leading-relaxed max-w-lg">{row.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTS carousel */}
      <section id="experts" className="relative py-16 border-y border-[#FAF0DE]/5 bg-[#0E1930]/40 backdrop-blur">
        <div className="text-center mb-8">
          <div className="text-xs font-mono uppercase tracking-[0.4em] text-[#FF8C42] mb-2">/ Experts suivis</div>
          <h3 className="font-display text-3xl md:text-4xl font-black">30+ voix qui font le SEO mondial.</h3>
        </div>
        <div className="overflow-hidden">
          <div className="flex gap-3 marquee-left" style={{ width: "max-content" }}>
            {[...EXPERT_NAMES, ...EXPERT_NAMES].map((e, i) => (
              <div key={i} className="shrink-0 rounded-2xl border border-[#FAF0DE]/10 bg-[#132038]/70 backdrop-blur px-5 py-3 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF8C42] to-[#FFD600] flex items-center justify-center font-display text-sm font-black text-[#0A1225]">
                  {e.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-display font-bold text-[#FAF0DE] leading-none">{e.name} <span className="text-sm">{e.country}</span></div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FAF0DE]/50 mt-1">{e.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFEST — replaced mascot with clean clipboard pose */}
      <section className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto rounded-[2rem] border border-[#FAF0DE]/10 bg-gradient-to-br from-[#132038] via-[#1B2B48] to-[#0A1225] overflow-hidden relative">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#FF8C42]/30 blur-3xl float-slow" />
          <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#4EA8FF]/20 blur-3xl float-med" />
          <div className="grid md:grid-cols-12 gap-10 p-10 md:p-16 relative z-10 items-center">
            <div className="md:col-span-7">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-6">/ Manifeste</div>
              <div className="font-display text-[6rem] md:text-[10rem] font-black leading-none text-shimmer mb-2">
                4000+
              </div>
              <p className="text-2xl md:text-3xl font-display font-bold text-[#FAF0DE]/90 max-w-lg leading-tight">
                modifs d'algo Google par an.<br />
                <span className="text-[#FAF0DE]/40">Vous n'avez plus le temps.</span>
              </p>
            </div>
            <div className="md:col-span-5 relative aspect-square">
              <div className="absolute inset-0 flex items-center justify-center">
                <Mascot pose="clipboard" className="w-72 h-72 md:w-80 md:h-80 bob" />
              </div>
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="absolute inset-0 rounded-full border float-slow"
                  style={{ borderColor: `rgba(255,140,66,${0.18 - i * 0.04})`, transform: `scale(${1 + i * 0.12})`, animationDelay: `${i * 1.2}s` }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER — Éléonore Bacher */}
      <section id="founder" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto rounded-[2rem] bg-[#FAF0DE] text-[#0A1225] overflow-hidden relative">
          <div className="grid md:grid-cols-12 gap-0">
            {/* Portrait side */}
            <div className="md:col-span-5 relative aspect-square md:aspect-auto bg-gradient-to-br from-[#FF8C42] via-[#FFA85C] to-[#FFD600] p-10 flex items-center justify-center overflow-hidden">
              <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden ring-8 ring-[#FAF0DE] shadow-2xl">
                <img
                  src="/founder/eleonore.jpg"
                  alt="Éléonore Bacher"
                  className="w-full h-full object-cover"
                  data-testid="founder-portrait"
                />
              </div>
              <div className="absolute top-6 left-6 chip bg-[#0A1225]/10 border-[#0A1225]/20 text-[#0A1225]">
                <Sparkles className="w-3 h-3" /> Fondatrice
              </div>
              <div className="absolute bottom-6 right-6 chip bg-[#0A1225]/10 border-[#0A1225]/20 text-[#0A1225]">
                Consultante SEO
              </div>
            </div>
            {/* Bio side */}
            <div className="md:col-span-7 p-10 md:p-14 flex flex-col justify-center">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#E56E1F] mb-4">
                / Derrière SEO Revue
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-none mb-4">
                Éléonore<br />Bacher.
              </h2>
              <div className="text-sm font-mono uppercase tracking-widest text-[#0A1225]/50 mb-6">
                Consultante SEO Freelance · 8+ ans
              </div>
              <p className="text-lg leading-relaxed text-[#0A1225]/80 mb-4 max-w-lg">
                Spécialiste <strong>SEO on-page &amp; off-page</strong>, <strong>maillage interne</strong>,
                <strong> cocon sémantique</strong>, <strong>Core Web Vitals</strong> et
                <strong> crawl budget optimization</strong>. Je pilote aussi bien un audit technique
                que des campagnes <strong>Google Ads</strong>, <strong>Meta Ads</strong> et <strong>LinkedIn Ads</strong>.
              </p>
              <p className="text-lg leading-relaxed text-[#0A1225]/80 mb-4 max-w-lg">
                Expertise en <strong>E-E-A-T</strong>, <strong>schema.org</strong>,
                <strong> netlinking stratégique</strong>, <strong>SEO local</strong> et
                <strong> optimisation SGE / AI Overviews</strong>. Environnements maîtrisés :
                WordPress, Shopify, Magento, Next.js.
              </p>
              <p className="text-lg leading-relaxed text-[#0A1225]/80 mb-8 max-w-lg">
                J'ai construit <strong>SEO Revue</strong> parce que j'avais moi-même
                besoin d'un radar central : trop de sources, pas assez de temps.
                Aujourd'hui, l'IA fait la veille — vous décidez.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:eleonorebacher8@gmail.com"
                  data-testid="founder-email-btn"
                  className="inline-flex items-center gap-3 bg-[#0A1225] text-[#FAF0DE] px-6 py-3.5 rounded-full font-mono text-xs uppercase tracking-widest hover:bg-[#FAF0DE] hover:text-[#0A1225] transition-all"
                >
                  <Mail className="w-3.5 h-3.5" /> Me contacter
                </a>
                <a
                  href="tel:+33677820849"
                  data-testid="founder-phone-btn"
                  className="inline-flex items-center gap-3 bg-[#0A1225]/5 border border-[#0A1225]/10 text-[#0A1225] px-6 py-3.5 rounded-full font-mono text-xs uppercase tracking-widest hover:bg-[#0A1225] hover:text-[#FAF0DE] transition-all"
                >
                  <Phone className="w-3.5 h-3.5" /> 06 77 82 08 49
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING (free beta) */}
      <section id="pricing" className="relative py-28 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">/ Accès</div>
          <h2 className="font-display text-5xl md:text-7xl font-black tracking-tight mb-4 leading-none">
            Gratuit <span className="text-[#FAF0DE]/40 font-medium">pendant la bêta</span>
          </h2>
          <p className="text-[#FAF0DE]/60 mb-14 max-w-md mx-auto">Aucune carte bancaire. Aucun engagement. Une seule offre : tout, pour tout le monde.</p>

          <div className="relative rounded-[2rem] border border-[#FAF0DE]/10 bg-[#132038]/80 backdrop-blur-2xl p-10 md:p-14 text-left overflow-hidden" data-testid="pricing-card">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FF8C42]/30 blur-3xl float-slow" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#4EA8FF]/20 blur-3xl float-med" />
            <div className="grid md:grid-cols-2 gap-10 relative z-10 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Mascot pose="lightbulb" className="w-14 h-14 bob" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FAF0DE]/50">SEO Revue</div>
                    <div className="font-display text-xl font-bold">Accès Bêta</div>
                  </div>
                </div>
                <button
                  onClick={loginWithGoogle}
                  data-testid="pricing-cta-btn"
                  className="w-full md:w-auto inline-flex items-center gap-3 bg-[#FF8C42] text-[#0A1225] px-7 py-4 rounded-full font-mono text-sm uppercase tracking-widest hover:bg-[#FAF0DE] transition-all hover:scale-105 shadow-[0_0_60px_-10px_rgba(255,140,66,0.8)]"
                >
                  Accéder gratuitement
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                <p className="mt-4 text-xs text-[#FAF0DE]/40 font-mono">Connexion Google · sans carte bancaire</p>
              </div>
              <div className="space-y-2.5">
                {[
                  { c: "#FF8C42", t: "Feed multi-sources · refresh 2h" },
                  { c: "#FFA85C", t: "Tendance quotidienne · Claude IA" },
                  { c: "#FFD600", t: "Récap hebdomadaire éditorial" },
                  { c: "#4EA8FF", t: "Alertes en temps réel" },
                  { c: "#E56E1F", t: "Timeline Core Updates" },
                  { c: "#FF8C42", t: "30+ experts internationaux" },
                  { c: "#4EA8FF", t: "Veille SGE · ChatGPT · Perplexity" },
                  { c: "#FFD600", t: "Favoris illimités" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-[#FAF0DE]/85">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: `${f.c}22`, border: `1px solid ${f.c}55` }}>
                      <Check className="w-3 h-3" style={{ color: f.c }} />
                    </div>
                    {f.t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with contact */}
      <footer className="relative border-t border-[#FAF0DE]/5 py-16 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8 mb-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Mascot pose="waving" className="w-10 h-10" />
              <Wordmark className="h-5" text="#FAF0DE" accent="#FF8C42" />
            </div>
            <p className="text-sm text-[#FAF0DE]/50 max-w-sm leading-relaxed">
              La veille SEO française qui pense à votre place. Made in France.
            </p>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">Site</div>
            <div className="space-y-2 text-sm">
              <a href="#categories" className="block text-[#FAF0DE]/60 hover:text-white">Catégories</a>
              <a href="#comment" className="block text-[#FAF0DE]/60 hover:text-white">Comment ça marche</a>
              <a href="/blog" className="block text-[#FAF0DE]/60 hover:text-white">Blog</a>
              <a href="#founder" className="block text-[#FAF0DE]/60 hover:text-white">Fondatrice</a>
              <a href="#pricing" className="block text-[#FAF0DE]/60 hover:text-white">Accès gratuit</a>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">Contact</div>
            <div className="space-y-2 text-sm">
              <a href="mailto:eleonorebacher8@gmail.com" data-testid="footer-email" className="flex items-center gap-2 text-[#FAF0DE]/60 hover:text-white">
                <Mail className="w-3.5 h-3.5" /> eleonorebacher8@gmail.com
              </a>
              <a href="tel:+33677820849" data-testid="footer-phone" className="flex items-center gap-2 text-[#FAF0DE]/60 hover:text-white">
                <Phone className="w-3.5 h-3.5" /> 06 77 82 08 49
              </a>
              <a href="https://eleonorebacher.fr/" target="_blank" rel="noopener noreferrer" data-testid="footer-web" className="flex items-center gap-2 text-[#FAF0DE]/60 hover:text-white">
                <ExternalLink className="w-3.5 h-3.5" /> eleonorebacher.fr
              </a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-6 border-t border-[#FAF0DE]/5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-[#FAF0DE]/40 font-mono">
          <div>© {new Date().getFullYear()} SEO Revue · Éléonore Bacher</div>
          <div className="uppercase tracking-widest">Made in France</div>
        </div>
      </footer>
    </div>
  );
}
